/**
 * Client-side diagnostics for login failures.
 *
 * Ships a structured diagnostic beacon for every failed login request
 * so the failure is visible in our logs.
 */
import {config} from '../config/config';
import {webFeature} from '../config/webFeature';

/**
 * Classify an axios exception into a coarse failure category.
 *   timeout          -> request aborted by our own timeout (hung connection)
 *   http_error       -> server responded with a 4xx/5xx
 *   network_or_cors  -> request made, no response (network down / CORS / DNS)
 *   client_error     -> something threw before/while building the request
 */
export function classifyError(exception) {
  if (!exception) {
    return {category: 'unknown'};
  }

  const code = exception.code;
  const timedOut = code === 'ECONNABORTED' || (exception.message && /timeout/i.test(exception.message));

  if (timedOut) {
    return {category: 'timeout', code};
  }
  if (exception.response) {
    return {category: 'http_error', status: exception.response.status, code};
  }
  if (exception.request) {
    return {category: 'network_or_cors', code};
  }
  return {category: 'client_error', code};
}

function safeHost(url) {
  try {
    return url ? new URL(url, window.location.origin).host : '';
  } catch (e) {
    return '';
  }
}

export function newRef() {
  const t = Date.now()
    .toString(36)
    .slice(-4);
  const r = Math.random()
    .toString(36)
    .slice(2, 6);
  return (t + r).toUpperCase();
}

// Build a same-origin URL for the existing /userError/report endpoint, reusing
// the api path (e.g. /login/lapi) but forcing the current page origin.
function getReportUrl() {
  let path = '/login/lapi';
  try {
    const apiLogin = (config.get('apiUrls') || {}).login;
    if (apiLogin) {
      path = new URL(apiLogin, window.location.origin).pathname;
    }
  } catch (e) {
    // keep default
  }
  return window.location.origin + path.replace(/\/+$/, '') + '/userError/report';
}

function buildDiagnostics(exception, context) {
  const info = classifyError(exception);
  const cfg = (exception && exception.config) || {};
  const realmConfig = config.get('realmConfig') || {};
  const apiHost = safeHost(cfg.url);

  return {
    category: info.category,
    status: info.status || null,
    code: info.code || null,
    failedUrl: cfg.url || null,
    method: cfg.method ? cfg.method.toUpperCase() : null,
    timeoutMs: cfg.timeout || null,
    apiHost: apiHost || null,
    pageOrigin: window.location.origin,
    pageHref: window.location.href,
    crossOrigin: apiHost ? apiHost !== window.location.host : null,
    clientHint: realmConfig['cocosite.client.hint'] || null,
    ssoMode: realmConfig['realm.sso.mode'] || null,
    // Reuse the caller's reference code (so the toast and this beacon match);
    // fall back to a fresh one for callers that don't show a toast.
    ref: (context && context.ref) || newRef(),
    flow: (context && context.flow) || null,
    extra: (context && context.extra) || null,
    userAgent: navigator.userAgent,
    time: new Date().toISOString(),
    message: exception && exception.message ? exception.message : String(exception),
  };
}

function sendToSentry(exception, diagnostics) {
  try {
    if (window.Sentry && typeof window.Sentry.captureException === 'function') {
      window.Sentry.captureException(exception, {extra: diagnostics});
    }
  } catch (e) {
    // ignore
  }
}

function sendBeacon(diagnostics) {
  const url = getReportUrl();
  const body =
    'errorMessage=' +
    encodeURIComponent(
      'login.' + diagnostics.category + ' [ref:' + (diagnostics.ref || '-') + ']: ' + (diagnostics.message || ''),
    ) +
    '&callStack=' +
    encodeURIComponent(JSON.stringify(diagnostics)) +
    '&url=' +
    encodeURIComponent(diagnostics.pageHref);

  // sendBeacon is non-blocking and survives navigation. Same-origin => no
  // preflight, and cookies are included so the server can attribute the user.
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], {type: 'application/x-www-form-urlencoded'});
      if (navigator.sendBeacon(url, blob)) {
        return;
      }
    }
  } catch (e) {
    // fall through to XHR
  }

  try {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');
    xhr.send(body);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Failed to send login error beacon', e);
  }
}

/**
 * Gate client-side login logging by the `login-verbose-logging` webfeature
 */
export function isVerboseLoggingEnabled() {
  try {
    return Boolean(webFeature.enabled('login-verbose-logging'));
  } catch (e) {
    return false;
  }
}

/**
 * Report a failed login-related request.
 * @param exception the axios error (or any Error)
 * @param context optional {flow: string, extra: object}
 */
export function reportLoginError(exception, context) {
  try {
    if (!isVerboseLoggingEnabled()) {
      return;
    }
    const diagnostics = buildDiagnostics(exception, context);
    // eslint-disable-next-line no-console
    console.log('login error', diagnostics);
    sendToSentry(exception, diagnostics);
    sendBeacon(diagnostics);
  } catch (e) {
    // Never let error reporting throw into the app.
    // eslint-disable-next-line no-console
    console.log('reportLoginError failed', e);
  }
}
